import React from 'react';
import { arrayOf, object, func } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { removeFromCompare, clearCompare } from './comparisonUtils';
import css from './CompareBar.module.css';

/**
 * CompareBar — sticky bottom bar showing listings queued for comparison.
 *
 * Usage (in App or SearchPage):
 *   {features.listingComparisons && compareSet.length > 0 && (
 *     <CompareBar
 *       listings={compareSet}
 *       onCompare={() => history.push('/compare')}
 *       onUpdate={newSet => setCompareSet(newSet)}
 *     />
 *   )}
 */
const CompareBar = ({ listings, onCompare, onUpdate, className }) => {
  if (listings.length === 0) return null;

  const handleRemove = (id) => {
    const updated = removeFromCompare(id);
    onUpdate && onUpdate(updated);
  };

  const handleClear = () => {
    clearCompare();
    onUpdate && onUpdate([]);
  };

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.inner}>
        <div className={css.listings}>
          {listings.map(l => (
            <div key={l.id} className={css.item}>
              <span className={css.title}>{l.title}</span>
              <button
                type="button"
                onClick={() => handleRemove(l.id)}
                className={css.removeBtn}
              >✕</button>
            </div>
          ))}
          {Array.from({ length: 3 - listings.length }).map((_, i) => (
            <div key={`empty-${i}`} className={classNames(css.item, css.empty)}>
              <span><FormattedMessage id="CompareBar.addMore" /></span>
            </div>
          ))}
        </div>
        <div className={css.actions}>
          <button
            type="button"
            onClick={onCompare}
            disabled={listings.length < 2}
            className={css.compareBtn}
          >
            <FormattedMessage id="CompareBar.compare" values={{ count: listings.length }} />
          </button>
          <button type="button" onClick={handleClear} className={css.clearBtn}>
            <FormattedMessage id="CompareBar.clear" />
          </button>
        </div>
      </div>
    </div>
  );
};

CompareBar.defaultProps = { onCompare: null, onUpdate: null, className: null };
CompareBar.propTypes = { listings: arrayOf(object).isRequired, onCompare: func, onUpdate: func };

export default CompareBar;
